import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_APPROVALS, GET_TINYCMS_ARTICLES, GET_TINYCMS_PAGES } from '../../graphql';
import { articleStatus, formatWhen } from './shared';
import { useAddons } from './addons';
import {
  channelName,
  dayTime,
  postText,
  postTime,
  REACH_CALENDAR,
  ReachPost,
} from '../reach/reach';

/**
 * TinyCMS → Today. The default page: what is waiting, what went out under the
 * rules, and what is scheduled. Built from the same article and approval
 * records the rest of the shell uses — not a separate activity feed.
 */

interface ArticleRow {
  id: string;
  title?: string;
  slug?: string;
  updatedAt?: string;
  state?: {
    live?: { version?: number } | null;
    scheduled?: { at?: string } | null;
    hasUnpublishedChanges?: boolean;
  };
}

interface ApprovalRow {
  id: string;
  kind: string;
  action: string;
  outcome: string;
  approvedBy?: string;
  versionNumber?: number;
  recordedAt?: string;
  via?: string;
}

const VERB: Record<string, string> = {
  publish: 'Published',
  schedule: 'Scheduled',
  unpublish: 'Took down',
};
const WHAT: Record<string, string> = {
  article: 'an article',
  page: 'a page',
  site: 'the site’s look and navigation',
  dataset: 'a dataset',
};

/** "Published a page · version 2" — a log row in words. */
function describeApproval(r: ApprovalRow) {
  const verb = VERB[r.action] || r.action;
  const what = WHAT[r.kind] || r.kind;
  return `${verb} ${what}${r.versionNumber ? ` · version ${r.versionNumber}` : ''}`;
}

function dayLabel(date = new Date()) {
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

export const CmsToday: React.FC = () => {
  const { accountId } = useAccount();
  const { data, loading } = useQuery(GET_TINYCMS_ARTICLES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: approvalsData } = useQuery(GET_TINYCMS_APPROVALS, {
    variables: { accountId, limit: 40 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  // Pages wait for approval too; they're reviewed on Site → Pages.
  const { data: pagesData } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const articles: ArticleRow[] = data?.tinycmsArticles || [];
  const pages: ArticleRow[] = pagesData?.tinycmsPages || [];
  const approvals: ApprovalRow[] = useMemo(
    () => approvalsData?.tinycmsApprovals || [],
    [approvalsData]
  );

  // Social posts an agent scheduled on an "Ask me first" channel wait here too.
  const { reachOn } = useAddons();
  const reachWindow = useMemo(() => {
    const from = new Date();
    return { from: from.toISOString(), to: new Date(from.getTime() + 86400000).toISOString() };
  }, []);
  const { data: reachData } = useQuery<{ reachCalendar: { waiting: ReachPost[] } }>(
    REACH_CALENDAR,
    {
      variables: { accountId, ...reachWindow },
      skip: !accountId || !reachOn,
      fetchPolicy: 'cache-and-network',
    }
  );
  const waitingPosts = reachData?.reachCalendar.waiting || [];

  const needsYes = (a: ArticleRow) => {
    const s = a.state;
    if (s?.scheduled) return false;
    return !s?.live || Boolean(s.hasUnpublishedChanges);
  };
  const waiting = [
    ...pages
      .filter(needsYes)
      .map((p) => ({ ...p, kind: 'Page', href: `/content?view=pages&page=${p.id}` })),
    ...articles.filter(needsYes).map((a) => ({ ...a, kind: 'Article', href: `/content/${a.id}` })),
    ...waitingPosts.map((p) => {
      const t = postTime(p);
      return {
        id: p.id,
        title: postText(p),
        kind: `Social post · ${channelName(p.platform)}`,
        label: 'Waiting',
        sub: t ? `Goes out ${dayTime(t)} once you approve it` : 'Goes out once you approve it',
        href: '/calendar',
        state: undefined,
        updatedAt: undefined,
      };
    }),
  ];
  const coming = articles.filter((a) => a.state?.scheduled);

  const ribbon = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return approvals
      .map((row) => {
        const at = row.recordedAt ? new Date(row.recordedAt) : null;
        if (!at || at < start) return null;
        const mins = at.getHours() * 60 + at.getMinutes();
        return {
          id: row.id,
          left: `${((mins / 1440) * 100).toFixed(2)}%`,
          color:
            row.outcome !== 'accepted'
              ? 'var(--ink-red)'
              : row.action === 'publish' || row.action === 'unpublish'
                ? 'var(--ink-green)'
                : 'var(--ink-accent)',
          h: row.action === 'publish' ? 72 : 36,
        };
      })
      .filter(Boolean) as Array<{ id: string; left: string; color: string; h: number }>;
  }, [approvals]);

  const now = new Date();
  const nowLeft = `${(((now.getHours() * 60 + now.getMinutes()) / 1440) * 100).toFixed(2)}%`;
  const ticks = [0, 6, 12, 18].map((h) => ({
    left: `${((h / 24) * 100).toFixed(2)}%`,
    label: h === 0 ? '12 AM' : h === 12 ? '12 PM' : `${h % 12} ${h < 12 ? 'AM' : 'PM'}`,
  }));

  return (
    <main className="ink-body">
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div className="ink-muted">{dayLabel()}</div>
          <h1 className="ink-display">
            {loading && !articles.length
              ? 'Today'
              : waiting.length
                ? waiting.length === 1
                  ? '1 thing needs you.'
                  : `${waiting.length} things need you.`
                : 'Nothing is waiting.'}
          </h1>
        </div>
        <Link to="/cms/history" style={{ font: '500 13.5px/1 var(--ink-font)' }}>
          Approval history →
        </Link>
      </div>

      <div
        style={{
          marginTop: 48,
          position: 'relative',
          height: 150,
          borderTop: '1px solid var(--ink-line)',
          borderBottom: '1px solid var(--ink-line)',
        }}
      >
        {ticks.map((t) => (
          <React.Fragment key={t.label}>
            <div
              style={{
                position: 'absolute',
                left: t.left,
                top: 0,
                bottom: 0,
                width: 1,
                background: 'var(--ink-line2)',
              }}
            />
            <div
              className="ink-mono"
              style={{
                position: 'absolute',
                left: t.left,
                bottom: -24,
                fontSize: 11.5,
                color: 'var(--ink-ink3)',
                transform: 'translateX(-50%)',
              }}
            >
              {t.label}
            </div>
          </React.Fragment>
        ))}
        {ribbon.map((m) => (
          <div
            key={m.id}
            style={{
              position: 'absolute',
              left: m.left,
              bottom: 0,
              width: 6,
              height: m.h,
              background: m.color,
              borderRadius: '3px 3px 0 0',
              transform: 'translateX(-50%)',
            }}
          />
        ))}
        <div
          style={{
            position: 'absolute',
            left: nowLeft,
            top: -10,
            bottom: 0,
            width: 1.5,
            background: 'var(--ink-ink)',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          gap: 18,
          marginTop: 40,
          font: '400 12px/1 var(--ink-font)',
          color: 'var(--ink-ink3)',
        }}
      >
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span
            className="ink-dot"
            style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--ink-green)' }}
          />
          Published
        </span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span
            className="ink-dot"
            style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--ink-accent)' }}
          />
          Other changes
        </span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span
            className="ink-dot"
            style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--ink-amber)' }}
          />
          Waiting on you
        </span>
      </div>

      {waiting.length ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 16,
            marginTop: 28,
          }}
        >
          {waiting.map((a) => {
            const status = articleStatus(a.state);
            return (
              <div
                key={a.id}
                className="ink-card"
                style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '16px 18px 18px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    flex: 1,
                  }}
                >
                  <div style={{ font: '500 11.5px/1 var(--ink-font)', color: 'var(--ink-amber)' }}>
                    {a.kind} · {'label' in a ? a.label : status.label}
                  </div>
                  <div style={{ font: '600 16px/1.3 var(--ink-font)' }}>
                    {a.title || 'Untitled'}
                  </div>
                  <div className="ink-muted">
                    {'sub' in a ? a.sub : `Edited ${formatWhen(a.updatedAt)}`}
                  </div>
                  <Link
                    to={a.href}
                    className="ink-btn-primary"
                    style={{
                      marginTop: 'auto',
                      textAlign: 'center',
                      textDecoration: 'none',
                      padding: '9px 16px',
                      borderRadius: 8,
                      color: 'var(--ink-inv-ink)',
                    }}
                  >
                    Review
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            marginTop: 28,
            border: '1px dashed var(--ink-line3)',
            borderRadius: 14,
            padding: 28,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <span
            className="ink-dot"
            style={{ width: 10, height: 10, background: 'var(--ink-green)' }}
          />
          <div>
            <div style={{ font: '600 16px/1.3 var(--ink-font)' }}>Nothing waiting on you</div>
            <div className="ink-muted" style={{ marginTop: 3 }}>
              Agents ask here before a draft or a change goes live.
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: 56,
          marginTop: 56,
        }}
      >
        <div>
          <div style={{ font: '600 16px/1.3 var(--ink-font)' }}>Recently approved</div>
          <div className="ink-muted" style={{ marginTop: 4 }}>
            Each one went live with a person’s yes. The full list is in Activity.
          </div>
          <div style={{ marginTop: 14 }}>
            {approvals
              .filter((r) => r.outcome === 'accepted')
              .slice(0, 6)
              .map((r) => (
                <div
                  key={r.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '110px minmax(0, 1fr)',
                    gap: 12,
                    padding: '13px 0',
                    borderTop: '1px solid var(--ink-line2)',
                  }}
                >
                  <span className="ink-mono" style={{ fontSize: 12, color: 'var(--ink-ink3)' }}>
                    {formatWhen(r.recordedAt)}
                  </span>
                  <div>
                    <div style={{ font: '500 14px/1.35 var(--ink-font)' }}>
                      {describeApproval(r)}
                    </div>
                    <div className="ink-muted">
                      {r.approvedBy} · {r.via === 'ui' ? 'website' : 'agent'}
                    </div>
                  </div>
                </div>
              ))}
            {!approvals.length ? <div className="ink-muted">No publish activity yet.</div> : null}
          </div>
        </div>
        <div>
          <div style={{ font: '600 16px/1.3 var(--ink-font)' }}>Coming up</div>
          <div className="ink-muted" style={{ marginTop: 4 }}>
            Scheduled to publish automatically.
          </div>
          <div style={{ marginTop: 14 }}>
            {coming.map((a) => (
              <Link
                key={a.id}
                to={`/content/${a.id}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr)',
                  gap: 12,
                  padding: '13px 0',
                  borderTop: '1px solid var(--ink-line2)',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <span style={{ font: '500 14px/1.35 var(--ink-font)' }}>
                  {a.title || 'Untitled'}
                </span>
                <span className="ink-mono" style={{ fontSize: 12, color: 'var(--ink-accent)' }}>
                  {formatWhen(a.state?.scheduled?.at)}
                </span>
              </Link>
            ))}
            {!coming.length ? <div className="ink-muted">Nothing scheduled.</div> : null}
          </div>
        </div>
      </div>
    </main>
  );
};
